/**
 * Challenge detail – show challenge info, submit one video per challenge, list submissions and vote.
 */
import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  getChallenge,
  getMyChallengeSubmission,
  subscribeChallengeSubmissions,
  voteChallengeSubmission,
  getVotedSubmissionIds,
} from '../../domain/usecases/challenges';
import { useUpload } from '../upload/UploadContext';
import { CHALLENGE_CATEGORY_LABELS } from '../../domain/entities/Challenge';
import Display from '../../shared/ui/Display.jsx';
import Body from '../../shared/ui/Body.jsx';
import Card from '../../shared/ui/Card.jsx';
import Label from '../../shared/ui/Label.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Button from '../../shared/ui/Button.jsx';
import PlateCard from '../../shared/ui/PlateCard.jsx';
import MedalGlyph from '../../shared/ui/MedalGlyph.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import styles from './ChallengeDetailPage.module.css';

export function ChallengeDetailPage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { progress, startChallengeUpload } = useUpload();
  const [challenge, setChallenge] = useState(null);
  const [mySubmission, setMySubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [votedIds, setVotedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [votingId, setVotingId] = useState(null);
  const [shareFeedback, setShareFeedback] = useState('');
  const fileInputRef = useRef(null);

  const challengeUrl = challengeId && typeof window !== 'undefined'
    ? `${window.location.origin}/challenges/${challengeId}`
    : '';

  async function handleShare() {
    if (!challengeUrl) return;
    const title = challenge?.title ? `${challenge.title} – BarChain Challenge` : 'BarChain Challenge';
    const text = challenge?.title ? `Check out "${challenge.title}" on BarChain` : 'Check out this challenge on BarChain';
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: challengeUrl });
        setShareFeedback('Shared!');
      } else {
        await navigator.clipboard.writeText(challengeUrl);
        setShareFeedback('Link copied!');
      }
      setTimeout(() => setShareFeedback(''), 2000);
    } catch (e) {
      if (e.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(challengeUrl);
          setShareFeedback('Link copied!');
        } catch {
          setShareFeedback('Could not share');
        }
        setTimeout(() => setShareFeedback(''), 2000);
      }
    }
  }

  useEffect(() => {
    if (!challengeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getChallenge(challengeId)
      .then((c) => {
        if (!cancelled) setChallenge(c);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load challenge');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [challengeId]);

  useEffect(() => {
    if (!challengeId || !userId) return;
    getMyChallengeSubmission(challengeId, userId).then(setMySubmission);
  }, [challengeId, userId]);

  useEffect(() => {
    if (!challengeId || !userId) {
      if (!userId) setSubmissions([]);
      return;
    }
    const unsub = subscribeChallengeSubmissions(challengeId, 100, setSubmissions);
    return () => unsub();
  }, [challengeId, userId]);

  useEffect(() => {
    if (!userId || submissions.length === 0) return;
    const ids = submissions.map((s) => s.id);
    getVotedSubmissionIds(userId, ids).then(setVotedIds);
  }, [userId, submissions]);

  useEffect(() => {
    if (progress?.type === 'challenge' && progress?.status === 'error') {
      setSubmitError(progress.error || 'Upload failed');
    }
    if (progress === null) setSubmitError(null);
  }, [progress]);

  async function handleSubmit(e) {
    const file = e?.target?.files?.[0];
    if (!file || !challengeId || !userId) return;
    if (!file.type.startsWith('video/')) {
      setSubmitError('Please select a video file.');
      return;
    }
    setSubmitError(null);
    try {
      await startChallengeUpload(challengeId, file);
      const updated = await getMyChallengeSubmission(challengeId, userId);
      setMySubmission(updated);
    } catch (err) {
      setSubmitError(err.message || 'Submit failed');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleVote(submissionId) {
    if (!userId || votingId) return;
    setVotingId(submissionId);
    try {
      await voteChallengeSubmission(submissionId, userId);
      setVotedIds((prev) => new Set([...prev, submissionId]));
    } finally {
      setVotingId(null);
    }
  }

  const endDateStr = challenge?.endDate
    ? new Date(challenge.endDate).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : '';
  const isActive = challenge && challenge.status === 'active' && (!challenge.endDate || new Date(challenge.endDate) > new Date());
  const isMySubmission = (s) => s.userId === userId;
  const challengeProgress = progress?.type === 'challenge' ? progress : null;

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton width="60%" height={44} radius="var(--radius-md)" style={{ marginBottom: '0.75rem' }} />
        <Skeleton width="100%" height={120} radius="var(--radius-md)" style={{ marginBottom: '0.75rem' }} />
        <Skeleton width="100%" height={280} radius="var(--radius-md)" />
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className={styles.page}>
        <Link to="/challenges" className={styles.back}>← Back to challenges</Link>
        <Body tone="secondary" style={{ color: 'var(--red-platform)', marginTop: '1rem' }}>
          {error || 'Challenge not found.'}
        </Body>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <Link to="/challenges" className={styles.back}>← Back to challenges</Link>
        <div className={styles.shareRow}>
          <Button variant="secondary" size="sm" onClick={handleShare}>Share</Button>
          {shareFeedback && <Body tone="secondary" style={{ fontSize: 13 }}>{shareFeedback}</Body>}
        </div>
      </div>

      <div className={styles.titleRow}>
        <Display as="h1" size="l">{challenge.title || 'Challenge'}</Display>
        <Badge variant={isActive ? 'red' : 'neutral'}>
          {isActive ? 'Live' : 'Ended'}
        </Badge>
      </div>

      {(challenge.category || endDateStr) && (
        <Body tone="secondary" style={{ marginBottom: '1rem' }}>
          {challenge.category && (CHALLENGE_CATEGORY_LABELS[challenge.category] || challenge.category)}
          {endDateStr && ` · Ends ${endDateStr}`}
        </Body>
      )}

      {challenge.description && (
        <Body tone="secondary" style={{ marginBottom: '1rem' }}>
          {challenge.description}
        </Body>
      )}

      {challenge.reward && (
        <Card frame="gold" style={{ marginBottom: '1rem' }}>
          <Label style={{ display: 'block', marginBottom: 4 }}>REWARD</Label>
          <Body>{challenge.reward}</Body>
        </Card>
      )}

      {challenge.rules && typeof challenge.rules === 'string' && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <Label style={{ display: 'block', marginBottom: 4 }}>RULES &amp; REQUIREMENTS</Label>
          <Body tone="secondary" style={{ whiteSpace: 'pre-wrap' }}>
            {challenge.rules}
          </Body>
        </Card>
      )}

      {/* Guest: only details + Login button */}
      {!userId ? (
        <Card style={{ marginBottom: '1.5rem' }}>
          <Body tone="secondary" style={{ marginBottom: '0.75rem' }}>
            Log in to see submitted videos, rate them, and submit your own video for this challenge.
          </Body>
          <Button variant="primary" size="lg" onClick={() => navigate('/login')}>
            Login
          </Button>
        </Card>
      ) : (
        <>
          {/* Submit section */}
          <section className={styles.section}>
            {mySubmission ? (
              <div>
                <Body tone="secondary" style={{ color: 'var(--gold)', marginBottom: '0.75rem' }}>
                  Your submission (1 video per challenge)
                </Body>
                <div className={styles.videoWrap}>
                  <video
                    src={mySubmission.videoUrl}
                    className={styles.video}
                    controls
                    playsInline
                  />
                </div>
              </div>
            ) : isActive ? (
              <div>
                <Body tone="secondary" style={{ marginBottom: '0.75rem' }}>
                  Submit one video for this challenge.
                </Body>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleSubmit}
                  className={styles.fileInputHidden}
                  disabled={!!challengeProgress}
                  aria-hidden="true"
                />
                <Button
                  variant="primary"
                  size="lg"
                  disabled={!!challengeProgress}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {challengeProgress ? 'Uploading…' : 'Submit video'}
                </Button>
                {challengeProgress && (
                  <Body tone="secondary" style={{ marginTop: '0.5rem' }}>
                    {challengeProgress.status === 'compressing' && `Compressing… ${challengeProgress.percent ?? 0}%`}
                    {challengeProgress.status === 'uploading' && `Uploading… ${challengeProgress.percent ?? 0}%`}
                    {challengeProgress.status === 'creating' && 'Creating submission…'}
                  </Body>
                )}
                {submitError && (
                  <Body tone="secondary" style={{ color: 'var(--red-platform)', marginTop: '0.5rem' }}>
                    {submitError}
                  </Body>
                )}
              </div>
            ) : (
              <Body tone="secondary">This challenge has ended. Submissions are closed.</Body>
            )}
          </section>

          {/* Submissions list */}
          <h2 className={styles.submissionsHeading}>Submissions</h2>
          {submissions.length === 0 ? (
            <EmptyState icon="flame" title="No submissions yet" body="Be the first to submit your video." />
          ) : (
            <div className={styles.submissionGrid}>
              {submissions.map((s) => {
                const isWinner = Boolean(s.isWinner);
                const voteFooter = !isMySubmission(s) ? (
                  votedIds.has(s.id) ? (
                    <Badge variant="gold">Voted</Badge>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!!votingId}
                      onClick={() => handleVote(s.id)}
                    >
                      {votingId === s.id ? 'Voting…' : 'Vote'}
                    </Button>
                  )
                ) : null;

                const footer = (
                  <div className={styles.submissionFooter}>
                    <Body tone="secondary" style={{ fontSize: 13 }}>
                      {s.voteCount} vote{s.voteCount !== 1 ? 's' : ''}
                    </Body>
                    {voteFooter}
                  </div>
                );

                return (
                  <PlateCard
                    key={s.id}
                    mediaSrc={s.videoUrl}
                    mediaType="video"
                    liftName={isMySubmission(s) ? 'Your submission' : 'Participant'}
                    weight=""
                    reps={null}
                    athlete=""
                    footer={footer}
                  >
                    {isWinner && (
                      <MedalGlyph name="challenge" size="lg" style={{ position: 'absolute', top: 8, right: 8 }} />
                    )}
                  </PlateCard>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
